import {
  Box,
  Button,
  Flex,
  Skeleton,
  Text,
  Tooltip,
  useMediaQuery,
  useOutsideClick,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';

import HelpComponent from 'components/CustomHelp';
import ArrowLeftBlocks from 'components/Icons/ArrowLeftBlocks';
import transformDimensions from 'connectors/transformDimensions';
import ErrorPage from 'pages/ErrorPage';
import { STATUS } from 'projectConstants';
import {
  fetchDimensionItems,
  fetchIndicatorDetail,
  fetchModelTime,
  geModelTimeStatus,
  getIndicatorDetail,
  getIndicatorDetailStatus,
  getModelTime,
  getTableShimmer,
} from 'redux/IndicatorPageSlice';
import { getCurrentDimensionIndex } from 'redux/InputPageSlice';
import {
  closeInputSection,
  fetchBlockDrivers,
  FetchPlangPageIndicatorTableData,
  getIndicatorTableData,
  getIndicatorTableDataStatus,
  getIndicatorTableDataError,
  getShowNonEditableColumns,
  getmultiDimFiltersItems,
  getSelectedDriver,
  onErrorShow,
  planPageShow,
  toggleShowNonEditableColumns,
  toggleExpand,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import QuestionMarkIcon from 'components/Icons/QuestionMarkIcon';
import { AppDispatch } from 'utils/GlobalHelpers';
import ErrorBoundary from 'components/ErrorBoundary';
import {
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  accent,
  controlHeight,
  fontSize,
  neutral,
  space,
  text as textToken,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import InfoIcon from 'pages/PlanRevamp/PlanPageUIRevamp/icons/InfoIcon';

import Chart from './Chart';
import IndicatorTable from './IndicatorTable';
import InputTypeGroupedButton from './InputTypeGroupedButton';
import MultiDimSetting from './MultiDimSetting';
import { monthNames } from './MultiDimensionTableOpzV2.utils';

export interface Properties {
  refetchRequest: () => void;
}

const isHeaderNonEditable = (header: any, date?: string): boolean => {
  if (header.is_dimension) return false;
  if (!date) return false;

  if (header.name.startsWith('FY')) {
    const scenarioYear = Number(date);
    if (!Number.isFinite(scenarioYear)) return false;
    return Number(header.name.slice(2, 4)) + 2000 < scenarioYear;
  }

  const headingParts = String(header.name).split('-');
  const scenarioParts = date.split('-');
  if (headingParts.length < 2 || scenarioParts.length < 2) return false;

  const headingYear = Number(headingParts[1]);
  const scenarioYear = Number(scenarioParts[1]);
  if (!Number.isFinite(headingYear) || !Number.isFinite(scenarioYear)) return false;

  if (headingYear < scenarioYear) return true;
  return (
    headingYear === scenarioYear &&
    monthNames.indexOf(headingParts[0]) <= monthNames.indexOf(scenarioParts[0])
  );
};

const ActualsToggleButton = ({ isVisible, isShowing, onClick }: any) => {
  if (!isVisible) return null;

  return (
    <Flex
      as='button'
      type='button'
      onClick={onClick}
      alignItems='center'
      h='28px'
      px='10px'
      mr='8px'
      border='1px solid'
      borderColor={isShowing ? accent.solid : neutral.border}
      borderRadius={PLAN_REVAMP_RADIUS_SM}
      bg={isShowing ? accent.subtle : neutral.surface}
      color={isShowing ? accent.solid : textToken.secondary}
      fontSize={fontSize.body}
      fontWeight={500}
      cursor='pointer'
      transition='background 0.12s ease, border-color 0.12s ease'
      _hover={{ borderColor: accent.solid }}
    >
      {isShowing ? 'Hide actuals' : 'Show actuals'}
    </Flex>
  );
};

/** Was a raw `<Image src={Expand}>` PNG — a bordered icon+text control, same
 * family as every other secondary control in the revamp, reads far more
 * intentional than a lone icon floating next to plain text. */
const ExpandToggleButton = ({
  isExpanded,
  onClick,
}: {
  isExpanded: boolean;
  onClick: () => void;
}) => (
  <Flex
    as='button'
    type='button'
    onClick={onClick}
    alignItems='center'
    gap='6px'
    h={controlHeight}
    px='10px'
    border='1px solid'
    borderColor={neutral.border}
    borderRadius={PLAN_REVAMP_RADIUS_SM}
    bg={neutral.surface}
    color={textToken.secondary}
    fontSize={fontSize.body}
    fontWeight={500}
    cursor='pointer'
    transition='background 0.12s ease, border-color 0.12s ease'
    _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
  >
    <Text as='span'>{isExpanded ? 'Collapse view' : 'Expand view'}</Text>
    {isExpanded ? <ChevronLeftIcon boxSize={4} /> : <ChevronRightIcon boxSize={4} />}
  </Flex>
);

const IndicatorPageMultiDim = ({ refetchRequest }: Properties) => {
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const containerReference = useRef<HTMLDivElement>(null);

  const [innerHeight, setInnerHeight] = useState(0);
  const [noDimChartData, setNoDimChartData] = useState<Record<string, Record<string, number>> | []>(
    [],
  );
  const currentDriver = useSelector(getSelectedDriver);
  const multiDimFiltersItems = useSelector(getmultiDimFiltersItems);

  const indicator = useSelector(getIndicatorDetail);
  const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
  const tabShimmer = useSelector(getTableShimmer);
  const modelTimeStatus = useSelector(geModelTimeStatus);
  const modelTime = useSelector(getModelTime);
  const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const showPlanPage = useSelector(planPageShow);
  const baseScenario = useSelector(getBaseScenario);
  const tableData = useSelector(getIndicatorTableData);
  const tableDataStatus = useSelector(getIndicatorTableDataStatus);
  const tableDataError = useSelector(getIndicatorTableDataError);
  const showNonEditableColumns = useSelector(getShowNonEditableColumns);

  const [isMobile] = useMediaQuery('(max-width: 1024px)');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const dimensionId = currentDriver?.dimensions[currentDimensionIndex];
  const tooltipReference = useRef<any>(null);

  const hasAnyNonEditableValueColumns = useMemo(() => {
    if (!tableData?.column_headers?.length) return false;

    const startDate = new Date(baseScenario?.start_date);
    startDate.setMonth(startDate.getMonth() - 1);
    const yearFormat = startDate.getFullYear();
    const monthFormat = `${monthNames[startDate.getMonth()]}-${String(yearFormat).slice(-2)}`;
    const targetDate = tableData?.type === 'raw' ? monthFormat : String(yearFormat);

    return tableData.column_headers.some((header: any) => isHeaderNonEditable(header, targetDate));
  }, [tableData?.column_headers, tableData?.type, baseScenario?.start_date]);

  useOutsideClick({
    ref: tooltipReference,

    handler: () => {
      setIsTooltipOpen(false);
    },
  });

  useLayoutEffect(() => {
    if (containerReference?.current) {
      const containerHeight = containerReference.current.offsetHeight;
      const containerPadding =
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
      const heightWithoutPadding = containerHeight - containerPadding;
      setInnerHeight(heightWithoutPadding);
    }
  }, [containerReference?.current]);

  const fetchModelTimeData = () => dispatch(fetchModelTime(Number(modelId)));

  const fetchDimItemsData = () => {
    dispatch(
      fetchDimensionItems({
        dimensionId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  useEffect(() => {
    if (modelId && blockId) {
      fetchModelTimeData();
    }
  }, [pathname, currentDriver]);

  useEffect(() => {
    if (currentDriver && currentDriver?.dimensions?.length > 0) {
      fetchDimItemsData();
    }
  }, [pathname, currentDriver?.id, baseScenario?.id, dimensionId]);

  useEffect(() => {
    if (currentDriver && baseScenario) {
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: currentDriver?.id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      );
    }
  }, [currentDriver, baseScenario?.id]);

  useEffect(() => {
    if (
      modelId &&
      blockId &&
      multiDimFiltersItems?.length === 0 &&
      tableData &&
      tableData?.column_headers
    ) {
      const chartFilterDimId = tableData?.column_headers[0].is_dimension
        ? tableData?.column_headers[0].id
        : null;
      dispatch(
        fetchIndicatorDetail({
          currentDriver,
          parameters: {
            dim_id: chartFilterDimId,
            is_chart_show: true,
            scenario_id: baseScenario?.id,
          },
        }),
      );
    }
  }, [currentDriver, baseScenario?.id, tableData]);

  useEffect(() => {
    if (indicator && !indicator?.output?.dims) {
      const object: any = {};
      object[indicator.output?.name] = indicator.output?.total_values;
      setNoDimChartData(object);
    }
  }, [indicator]);

  useEffect(() => {
    if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
      dispatch(onErrorShow(true));
    }
  }, [indicatorDetailStatus, modelTimeStatus]);

  const toggle = () => dispatch(toggleExpand());

  const closeSection = () => {
    dispatch(closeInputSection());

    if (!showPlanPage) {
      toggle();
    }
  };

  const showShimmer = indicatorDetailStatus !== STATUS.SUCCESS || tabShimmer === true;
  if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
    return (
      <>
        {isMobile && (
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            p={4}
            backgroundColor={'transparent'}
          >
            <Flex onClick={closeSection} alignItems='center' cursor='pointer'>
              {isMobile && (
                <Box mr='6px' fontFamily={'regular_roboto'}>
                  <ArrowLeftBlocks />
                </Box>
              )}
              {isMobile ? (
                <Text size='assistant_md'>Inputs</Text>
              ) : (
                <Flex alignItems={'center'}>
                  <Text ml={3} size='assistant_md'>
                    {indicator?.output?.name}
                  </Text>
                </Flex>
              )}
            </Flex>
            <CloseIcon boxSize='14px' cursor='pointer' onClick={closeSection} />
          </Flex>
        )}
        <Flex alignItems={'center'} justifyContent={'center'} height={'inherit'}>
          <ErrorPage planError={true} />
        </Flex>
      </>
    );
  }

  const refetchDrivers = async () => {
    await dispatch(
      fetchBlockDrivers({
        blockId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  const refetchData = async () => {
    fetchModelTimeData();
    if (currentDriver && currentDriver.dimensions.length > 0) {
      fetchDimItemsData();
    }
    refetchRequest();
  };

  // Show ErrorBoundary fallback if there's an API error
  if (tableDataStatus === STATUS.FAILED) {
    return (
      <Flex direction='column' align='center' justify='center' h='100vh' p={6}>
        <Text fontSize='xl' fontWeight='bold' color='red.600' mb={4}>
          Failed to Load Indicator Data
        </Text>
        <Text fontSize='md' color='gray.600' textAlign='center' maxW='500px' mb={6}>
          {tableDataError || 'Unable to fetch indicator table data. Please try again.'}
        </Text>
        <Button
          size='md'
          colorScheme='blue'
          onClick={() => {
            if (currentDriver && baseScenario) {
              dispatch(
                FetchPlangPageIndicatorTableData({
                  indicator_id: currentDriver?.id,
                  parameters: {
                    scenario_id: baseScenario?.id,
                    format_excel_data: true,
                  },
                }),
              );
            }
          }}
        >
          Retry
        </Button>
      </Flex>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <Flex direction='column' align='center' justify='center' h='100vh' p={6}>
          <Text fontSize='xl' fontWeight='bold' color='red.600' mb={4}>
            Something went wrong with the Indicator Page
          </Text>
          <Text fontSize='md' color='gray.600' textAlign='center' maxW='500px' mb={6}>
            {tableDataError ||
              'We encountered an unexpected error while loading the indicator data. This might be due to a network issue or a temporary problem with the data source.'}
          </Text>
          <Button
            size='md'
            colorScheme='blue'
            onClick={() => {
              if (currentDriver && baseScenario) {
                dispatch(
                  FetchPlangPageIndicatorTableData({
                    indicator_id: currentDriver?.id,
                    parameters: {
                      scenario_id: baseScenario?.id,
                      format_excel_data: true,
                    },
                  }),
                );
              } else {
                window.location.reload();
              }
            }}
          >
            {tableDataError ? 'Retry' : 'Reload Page'}
          </Button>
        </Flex>
      }
    >
      <Flex
        display='flex'
        flexDirection='column'
        h='100vh'
        width='100%'
        overflowY='auto'
        px={PLAN_HEADER_HORIZONTAL_PADDING}
        py='12px'
        pb='0 !important'
      >
        <Flex justifyContent='space-between' alignItems='center'>
          <Flex onClick={closeSection} alignItems='center' gap='2px' cursor='pointer'>
            {isMobile && (
              <Box mr='6px' fontFamily='regular_roboto'>
                <ArrowLeftBlocks />
              </Box>
            )}
            {isMobile ? (
              <Text {...PLAN_TEXT.panelTitle} color={textToken.primary}>
                Inputs
              </Text>
            ) : (
              <>
                <ChevronLeftIcon boxSize={4} color={textToken.secondary} />
                <Flex
                  alignItems='center'
                  gap='2'
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <Text {...PLAN_TEXT.panelTitle} color={textToken.primary} noOfLines={1}>
                    {indicator?.output?.name}
                  </Text>
                  {indicator?.help && (
                    <Tooltip
                      isOpen={isTooltipOpen}
                      hasArrow
                      arrowSize={10}
                      bg='white'
                      placement='right'
                      boxShadow='rgba(0, 0, 0, 0.24) 0px 3px 8px'
                      label={
                        <VStack
                          bg='white'
                          h='inherit'
                          p='3'
                          justify='space-between'
                          maxWidth='300px'
                          width='180px'
                          alignItems='center'
                          alignContent='center'
                          gap={1}
                          zIndex={1}
                        >
                          <Text
                            mb='2.5'
                            fontFamily='bold_roboto'
                            fontSize='sm'
                            lineHeight='4'
                            noOfLines={2}
                            color='black'
                          >
                            Help
                          </Text>
                          <Text
                            size='modalContent'
                            fontFamily='regular_roboto'
                            noOfLines={3}
                            color='black'
                            fontSize='2xs'
                          >
                            {indicator?.help}
                          </Text>
                        </VStack>
                      }
                      borderRadius='4'
                      width='-moz-min-content'
                    >
                      <Box
                        onMouseEnter={() => setIsTooltipOpen(true)}
                        onMouseLeave={() => setIsTooltipOpen(false)}
                        ref={tooltipReference}
                      >
                        <QuestionMarkIcon height={23} width={23} color='white' />
                      </Box>
                    </Tooltip>
                  )}
                </Flex>
              </>
            )}
          </Flex>
          <Flex
            as='button'
            type='button'
            aria-label='Close inputs panel'
            alignItems='center'
            justifyContent='center'
            h='28px'
            w='28px'
            minW='28px'
            flexShrink={0}
            border='1px solid'
            borderColor={neutral.border}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            bg={neutral.surface}
            color={textToken.secondary}
            cursor='pointer'
            transition='background 0.12s ease, border-color 0.12s ease, color 0.12s ease'
            _hover={{
              bg: neutral.surfaceHover,
              borderColor: neutral.borderStrong,
              color: accent.solid,
            }}
            onClick={closeSection}
          >
            <CloseIcon boxSize='11px' />
          </Flex>
        </Flex>
        {isMobile && (
          <Flex alignItems='center' mt={1}>
            <Text {...PLAN_TEXT.panelTitle} color={textToken.primary} ml={3} noOfLines={1}>
              {indicator?.output?.name}
            </Text>
          </Flex>
        )}
        {showShimmer && (
          <Box
            h={innerHeight ? `${innerHeight}px` : '30vh'}
            pt={4}
            minH={innerHeight ? `${innerHeight}px` : '30vh'}
            mb='3'
          >
            <Skeleton startColor='white' endColor='#CCD4E8' h={'-webkit-fill-available'} />
          </Box>
        )}

        {!showShimmer && !showPlanPage && (
          <Box display={{ base: 'block' }} ref={containerReference} height={'34vh'} mb={space.xl}>
            {indicator?.output?.dims ? (
              <Chart
                showLabel={true}
                chartLabelData={indicator?.output.total_values}
                chartData={indicator?.output?.dim_item_data}
                chartType={indicator?.output?.chart_type}
                dimId={indicator?.output?.dims?.id}
                formatProperties={indicator?.output?.data_format_properties}
                axisTheme='light'
              />
            ) : (
              <Chart
                showLabel={true}
                chartLabelData={indicator?.output?.total_values}
                chartData={noDimChartData}
                chartType={indicator?.output?.chart_type}
                formatProperties={indicator?.output?.data_format_properties}
                axisTheme='light'
              />
            )}
          </Box>
        )}

        <Flex direction='column' borderTopLeftRadius='base' borderTopRightRadius='base'>
          {!showPlanPage && (
            <Flex alignItems={'center'} justifyContent={'space-between'} w={'100%'}>
              <Flex w={'100%'} justifyContent={'space-between'}>
                {/* Content-sized, not 60/70/30 percentages: the segmented
                    control is `fit-content`, so a 70%-wide wrapper reserved
                    far more room than it used and the leftover showed up as a
                    gap before the dimension picker. */}
                <Flex align={'center'} gap={3} minW={0}>
                  {currentDriver.dimensions && (
                    <InputTypeGroupedButton
                      inputDimensions={transformDimensions(currentDriver.dimensions)}
                      blockId={currentDriver.block_id}
                      inputType={tableData?.type}
                      indicatorId={indicator?.id}
                      refetchDrivers={refetchDrivers}
                      inputDataValues={tableData?.data_values}
                      timeDimension={currentDriver.time_dim}
                      timeRange={modelTime?.time_range}
                      timeRangeYears={modelTime?.time_range_years}
                      refetchRequest={refetchData}
                    />
                  )}
                  <MultiDimSetting indicatorId={indicator?.id} refetchRequest={refetchData} />
                </Flex>
                <Flex direction='column' alignItems='flex-end' gap='6px'>
                  <Flex alignItems='center' gap={2}>
                    <ActualsToggleButton
                      isVisible={hasAnyNonEditableValueColumns}
                      isShowing={showNonEditableColumns}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        dispatch(toggleShowNonEditableColumns());
                      }}
                    />
                    <ExpandToggleButton isExpanded onClick={toggle} />
                  </Flex>
                  <Flex alignItems='center' gap='4px'>
                    <InfoIcon width={12} height={12} color={textToken.muted} />
                    <Text {...PLAN_TEXT.labelSm} color={textToken.secondary}>
                      Click and drag cells to select multiple periods
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          )}
          {showPlanPage && (
            <>
              <Box mb={space.md}>
                {currentDriver?.dimensions && (
                  <InputTypeGroupedButton
                    inputDimensions={transformDimensions(currentDriver.dimensions)}
                    blockId={currentDriver.block_id}
                    inputType={tableData?.type}
                    indicatorId={indicator?.id}
                    refetchDrivers={refetchDrivers}
                    inputDataValues={tableData?.data_values}
                    timeDimension={currentDriver.time_dim}
                    timeRange={modelTime?.time_range}
                    timeRangeYears={modelTime?.time_range_years}
                    refetchRequest={refetchData}
                  />
                )}
              </Box>
              {isMobile && (
                <Flex align={'center'} gap={2} mb={space.md}>
                  <ActualsToggleButton
                    isVisible={hasAnyNonEditableValueColumns}
                    isShowing={showNonEditableColumns}
                    onClick={() => dispatch(toggleShowNonEditableColumns())}
                  />
                  <MultiDimSetting indicatorId={indicator?.id} refetchRequest={refetchData} />
                </Flex>
              )}
              <Box
                display={{ base: 'none', md: 'flex' }}
                width='100%'
                justifyContent={'space-between'}
                alignItems={'center'}
                cursor={'pointer'}
                mb={space.lg}
              >
                <Flex align={'center'} gap={2}>
                  <MultiDimSetting indicatorId={indicator?.id} refetchRequest={refetchData} />
                </Flex>
                <Flex align='center' gap={2}>
                  <ActualsToggleButton
                    isVisible={hasAnyNonEditableValueColumns}
                    isShowing={showNonEditableColumns}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      dispatch(toggleShowNonEditableColumns());
                    }}
                  />
                  <ExpandToggleButton isExpanded={false} onClick={toggle} />
                </Flex>
              </Box>
            </>
          )}

          {isMobile ? (
            <>
              <Box mt='1' mb='1'>
                <IndicatorTable
                  refetchRequest={refetchData}
                  isPercentage={indicator?.data_format.toLowerCase()}
                />
              </Box>
              {indicator?.help && (
                <Box mt={5} pb={3}>
                  <HelpComponent helpText={indicator?.help} style={{ width: '100%' }} />
                </Box>
              )}
            </>
          ) : (
            <>
              <Box overflowY='auto' maxH={'100%'}>
                <IndicatorTable
                  refetchRequest={refetchData}
                  isPercentage={indicator?.data_format.toLowerCase()}
                />
                {/* {indicator?.help && (
                <Box mt={5} pb={3}>
                  <HelpComponent
                    helpText={indicator?.help}
                    style={{ width: { base: '98%', '8xl': '380px' } }}
                  />
                </Box>
              )} */}
              </Box>
            </>
          )}
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

export default IndicatorPageMultiDim;
